<script lang="ts">
  import { onMount } from "svelte"
  import { xhr, toast, wait } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import Navigation from "./Navigation.svelte"
  import { onboardingStore } from "./onboarding.svelte"
  import SwotInline from "$app/Pages/Plans/Onboarding/components/SwotInline.svelte"
  import PrevNextSkipButtons from "$app/Pages/Plans/Onboarding/components/PrevNextSkipButtons.svelte"
  import { SquarePlus } from "lucide-svelte"
  import { appStore } from "$app/store.svelte"
  import bg from "@images/onboarding/bg-eclipse.jpg"
  import img1 from "@images/onboarding/strengths.png"
  import img2 from "@images/onboarding/weaknesses.png"
  import img3 from "@images/onboarding/opportunities.png"
  import img4 from "@images/onboarding/threats.png"

  const images = {
    strengths: img1,
    weaknesses: img2,
    opportunities: img3,
    threats: img4
  }

  const img = images[onboardingStore.current_onboarding_step]
  const limit = 3

  const currentSwot = onboardingStore.swotOptions.find(
    (s) => s.name === onboardingStore.current_onboarding_step
  )

  let swots: StatementTypeExt[] = $state([])
  let loaded = $state(false)
  onboardingStore.completed = true

  async function getSwots(): Promise<void> {
    try {
      const resp = await xhr.get(
        `/plan_onboarding/${appStore.plan.id}/get_statements.json?statement_type=${onboardingStore.current_onboarding_step}`
      )

      let swotData = resp.data
        .map((swot) => {
          return { ...swot, _selected: false, _valid: !!swot.id }
        })
        .sort((a, b) => a.position - b.position) as StatementTypeExt[]

      swots = swotData
    } catch (e) {
      console.error(e)
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading the strengths", "error")
    } finally {
      loaded = true
    }
  }

  async function deleteSwot(swot: StatementTypeExt): Promise<void> {
    swot._selected = true

    if (!swot.id) {
      const idx = swots.findIndex((s) => s.id === swot.id)
      swots.splice(idx, 1)
      swots = [...swots]

      return
    }

    if (!confirm(`Are you sure you want delete the selected entry`)) {
      swot._selected = false
      swots = swots
      return
    }

    try {
      await xhr.delete(`/plans/${appStore.plan.id}/statements/${swot.id}.json`)
      const idx = swots.findIndex((s) => s.id === swot.id)

      swots.splice(idx, 1)
      swots = swots
    } catch (e) {
      toast(`There was an error deleting the ${swot.section_type}`, "error")
      console.error(e)
    }
  }

  function addEmptySwot(): void {
    let updatedSwots = [...swots, emptySwot]
    swots = updatedSwots
  }

  const sectType: string =
    {
      strengths: "Strength",
      weaknesses: "Weakness",
      opportunities: "Opportunity",
      threats: "Threat"
    }[onboardingStore.current_onboarding_step] || ""

  const emptySwot = {
    name: "",
    id: null,
    section_type: sectType,
    display: true,
    _selected: false,
    _valid: false
  } as StatementTypeExt

  onMount(() => {
    getSwots()
    onboardingStore.setBrowserHistory(
      {},
      onboardingStore.current_onboarding_step
    )
  })
</script>

<div class="mx-auto px-2 lg:px-6 pt-4 w-full pb-8 lg:pb-20 lg:text-left">
  <div class="px-4 w-full">
    <Navigation step={onboardingStore.current_onboarding_step} />
  </div>
  <div class="px-4 w-full lg:w-1/2 xl:w-5/12 xl:pr-16">
    <h2 class="mt-12 text-5xl font-bold text-left text-white">
      {currentSwot?.title}
    </h2>

    <p class="mt-8 text-white text-lg max-w-lg">
      {currentSwot?.description}
    </p>

    <p class="mt-4 text-white text-base max-w-lg">
      Please enter at least
      <span class="font-bold">2 {onboardingStore.current_onboarding_step}</span>
      in order to continue.
    </p>

    {#if loaded && swots}
      <ul class={onboardingStore.current_onboarding_step}>
        {#each swots as swot, idx}
          <SwotInline
            {swot}
            lineNumber={idx + 1}
            ondelete={(swot) => deleteSwot(swot)}
            onupdate={(swot) => {
              swots[idx] = swot
              swots = swots
            }}
            onprocess={(data) => {
              swot[idx] = data
            }}
          />
        {/each}
      </ul>
    {/if}
    <div class="px-3 py-2">
      {#if swots.length < limit}
        <button
          class="text-white font-semibold hover:text-blue-200 flex items-center py-1 px-2"
          onclick={addEmptySwot}
        >
          <SquarePlus class="inline size-5 mr-2" />
          Add {sectType}
        </button>
      {/if}
    </div>
  </div>
  <PrevNextSkipButtons />
</div>
<div
  class="relative w-full hidden lg:inline lg:absolute lg:inset-y-32 lg:-mb-32 rounded-tl-lg lg:-right-0 md:w-1/3 lg:w-1/2 xl:w-7/12 lg:h-full bg-lwp-light-grey bg-right-bottom bg-no-repeat bg-contain"
  style="background-image: url({bg})"
>
  <img
    src={img}
    alt={onboardingStore.current_onboarding_step}
    class="absolute shadow-lg rounded border border-gray-500 inset-y-3 inset-x-6 xl:-inset-x-3 w-11/12 object-fill"
  />
</div>
